import { type FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { AmbientBackground } from '@/components/layout/AmbientBackground';
import { Button } from '@/components/ui/Button';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { AuthSiteBar } from '@/components/layout/AuthSiteBar';
import { Spinner } from '@/components/ui/Spinner';
import { loadRememberedEmail, loadRememberMePref } from '@/auth/storage';
import { brandLang, useI18n } from '@/i18n';

export function SignInPage() {
  const { signIn, isAuthenticated } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState(() => loadRememberedEmail());
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => loadRememberMePref());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email.trim(), password, rememberMe);
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('auth.signInError');
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <AmbientBackground />
      <div className="auth-page__frame">
        <div className="auth-card glass-panel">
          <div className="auth-card__mark">
            <img src="/brand/richyplay-logo.png" alt="RichyPlay" width={220} height={39} />
          </div>
          <h1 className="auth-card__title display" lang={brandLang(t('auth.title'))}>
            {t('auth.title')}
          </h1>
          <p className="auth-card__subtitle">{t('auth.subtitle')}</p>

          <form className="auth-form" onSubmit={handleSubmit}>
            <label className="field">
              <span className="field__label">{t('auth.email')}</span>
              <input
                className="contact-input"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('auth.password')}</span>
              <input
                className="contact-input"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label className="auth-form__remember">
              <input
                type="checkbox"
                name="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>{t('auth.rememberMe')}</span>
            </label>

            {error ? <ErrorMessage message={error} title={t('auth.signInFailed')} /> : null}

            <Button type="submit" className="btn-primary auth-form__submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : t('auth.signIn')}
            </Button>
          </form>

          <p className="auth-card__footer">
            <Link to="/forgot-password" className="auth-link">
              {t('auth.forgotPassword')}
            </Link>
          </p>
        </div>
        <p className="auth-page__note">{t('auth.inviteOnly')}</p>
        <AuthSiteBar />
      </div>
    </div>
  );
}
